import React, { useEffect, useState } from "react";
import {
  Button,
  Card,
  Col,
  Form,
  Input,
  Row,
  Select,
  Switch,
  Table,
  Typography,
  Modal,
} from "antd";
import useRequest from "../../hooks/useRequest";
import { Severty, ShowToast } from "../../helper/toast";
import { EditOutlined, EyeOutlined } from "@ant-design/icons";

const { Title } = Typography;
const { Option } = Select;

const roles = ["Admin", "Ops", "Vendor", "Client"];
const statusOptions = ["Active", "Inactive"];

const ManageAccounts = () => {
  const { request } = useRequest();
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [form] = Form.useForm();
  const [editingUser, setEditingUser] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [viewModalVisible, setViewModalVisible] = useState(false);
  const [viewingUser, setViewingUser] = useState(null);

  // Fetch users with pagination
  const fetchUsers = (page = 1, pageSize = 10) => {
    setLoading(true);
    request({
      url: `/user/user_list?page=${page}&page_size=${pageSize}`,
      method: "GET",
      onSuccess: (response) => {
        setUsers(response?.data || []);
        setPagination({
          current: response?.pagination?.current_page || 1,
          pageSize: response?.pagination?.page_size || 10,
          total: response?.pagination?.total_count || 0,
        });
        setLoading(false);
      },
      onError: () => {
        setLoading(false);
        ShowToast("Failed to fetch users", Severty.ERROR);
      },
    });
  };

  useEffect(() => {
    fetchUsers(pagination.current, pagination.pageSize);
  }, []);

  // Handle page change
  const handleTableChange = (pagination) => {
    fetchUsers(pagination.current, pagination.pageSize);
  };

  const openModal = (user = null) => {
    setEditingUser(user);
    form.resetFields();
    if (user) form.setFieldsValue(user);
    setModalVisible(true);
  };

  const onFinish = (values) => {
    setLoading(true);
    const payload = {
      ...values,
      status: values.status ? 1 : 0,
    };

    const apiCall = editingUser
      ? request({
          url: `/user/update_user`,
          method: "PUT",
          data: { ...payload, id: editingUser.id },
          onSuccess: () => {
            ShowToast("User updated successfully", Severty.SUCCESS);
            fetchUsers(pagination.current, pagination.pageSize);
            setModalVisible(false);
            setLoading(false);
          },
          onError: (err) => {
            ShowToast(err?.response?.data?.error, Severty.ERROR);
            setLoading(false);
          },
        })
      : request({
          url: "/user/create_user",
          method: "POST",
          data: payload,
          onSuccess: () => {
            ShowToast(
              "User added successfully. Welcome email sent!",
              Severty.SUCCESS
            );
            fetchUsers(pagination.current, pagination.pageSize);
            setModalVisible(false);
            setLoading(false);
          },
          onError: (err) => {
            ShowToast(err?.response?.data?.error, Severty.ERROR);
            setLoading(false);
          },
        });
  };

  const toggleStatus = (record) => {
    const newStatus = record.status === 1 ? 0 : 1;
    request({
      url: `/user/update_status`,
      method: "PUT",
      data: {
        id: record.id,
        status: newStatus,
      },
      onSuccess: () => {
        ShowToast("User status updated successfully", Severty.SUCCESS);
        fetchUsers(pagination.current, pagination.pageSize);
      },
      onError: (err) => ShowToast(err?.response?.data?.message, Severty.ERROR),
    });
  };

  const columns = [
    { title: "Name", dataIndex: "name", key: "name" },
    { title: "Email", dataIndex: "email", key: "email" },
    { title: "Role", dataIndex: "role", key: "role" },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (text, record) => (
        <Switch checked={text === 1} onChange={() => toggleStatus(record)} />
      ),
    },
    {
      title: "Action",
      key: "action",
      render: (_, record) => (
        <div className="d-flex gap-2">
          <Button onClick={() => openModal(record)}>
            <EditOutlined />
          </Button>
          <Button
            onClick={() => {
              setViewingUser(record);
              setViewModalVisible(true);
            }}
          >
           <EyeOutlined />
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="layout-content">
      <Card>
        <Row justify="space-between" align="middle">
          <Col>
            <Title level={3}>Manage Accounts</Title>
          </Col>
          <Col>
            <Button type="primary" onClick={() => openModal()}>
              Add New User
            </Button>
          </Col>
        </Row>

        <Table
          columns={columns}
          dataSource={users}
          rowKey="id"
          loading={loading}
          pagination={pagination}
          onChange={handleTableChange}
          className="mt-3"
        />
      </Card>

      {/* Add/Edit User Modal */}
      <Modal
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        className="tab_modal"
      >
        <div className="modal_header">
{editingUser ? "Edit User" : "Add New User"}
        </div>
        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
          initialValues={{ role: "Client", status: true }}
        >
          <Form.Item
            label="Name"
            name="name"
            rules={[{ required: true, message: "Please enter name" }]}
          >
            <Input placeholder="Name" />
          </Form.Item>

          <Form.Item
            label="Email"
            name="email"
            rules={[
              { required: true, message: "Please enter email" },
              { type: "email", message: "Please enter a valid email" },
            ]}
            disabled={!!editingUser}
          >
            <Input placeholder="Email" />
          </Form.Item>

          <Form.Item
            label="Role"
            name="role"
            rules={[{ required: true, message: "Please select role" }]}
          >
            <Select>
              {roles.map((role) => (
                <Option key={role} value={role}>
                  {role}
                </Option>
              ))}
            </Select>
          </Form.Item>

         

          <Form.Item>
            <div className="modal_footer">
              <Button type="primary" htmlType="submit" loading={loading}>
              {editingUser ? "Update User" : "Create User"}
            </Button>
            </div>
          </Form.Item>
        </Form>
      </Modal>

      {/* View User Modal */}
      <Modal
        open={viewModalVisible}
        onCancel={() => setViewModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setViewModalVisible(false)}>
            Close
          </Button>,
        ]}
        className="tab_modal"
      >
        <div className="modal_header">
User Details
        </div>
        {viewingUser && (
          <div>
            <Row gutter={[16, 16]}>
              <Col span={12}>
                <strong>Name:</strong> {viewingUser.name}
              </Col>
              <Col span={12}>
                <strong>Email:</strong> {viewingUser.email}
              </Col>
              <Col span={12}>
                <strong>Role:</strong> {viewingUser.role}
              </Col>
              <Col span={12}>
                <strong>Status:</strong>{" "}
                {viewingUser.status === 1 ? "Active" : "Inactive"}
              </Col>
              <Col span={12}>
                <strong>Superuser:</strong>{" "}
                {viewingUser.is_superuser ? "Yes" : "No"}
              </Col>
              <Col span={12}>
                <strong>Email Verified:</strong>{" "}
                {viewingUser.is_email_verified ? "Yes" : "No"}
              </Col>
              <Col span={12}>
                <strong>Approved:</strong>{" "}
                {viewingUser.is_approved ? "Yes" : "No"}
              </Col>
              <Col span={12}>
                <strong>Phone:</strong>{" "}
                {viewingUser.phone_number || "Not provided"}
              </Col>
              {viewingUser.user_detail && (
                <>
                  <Col span={12}>
                    <strong>Company:</strong>{" "}
                    {viewingUser.user_detail.company || "Not provided"}
                  </Col>
                  <Col span={12}>
                    <strong>Language:</strong>{" "}
                    {viewingUser.user_detail.preferred_language || "Not set"}
                  </Col>
                  <Col span={24}>
                    <strong>Created At:</strong>{" "}
                    {new Date(
                      viewingUser.user_detail.created_at
                    ).toLocaleString()}
                  </Col>
                </>
              )}
            </Row>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default ManageAccounts;